/**
 * createProfileStore — the app-wide memory of the public `profiles/{uid}`
 * projection (a climber's name and avatar).
 *
 * Before this, every surface kept its own copy, and the gym-wide one in
 * `useGymData` read each uid once for the whole session. A climber who renamed
 * themselves stayed under the old name on the leaderboard until the viewer
 * reloaded the app, while the admin users list — which reads again on every
 * opening — already showed the new one: the same person under two names.
 *
 * - A found profile is fresh for `ttlMs`; the next request after that reads it
 *   again. A missing one is remembered for the much shorter `missingTtlMs`:
 *   the `syncPublicProfile` trigger writes a new account's profile seconds
 *   after sign-up, so "missing" must not stick. A failed read is not
 *   remembered at all, so the next request is a real retry.
 * - One read per uid at a time: a second caller asking for a uid that is
 *   already being read waits for the same answer.
 * - A re-read whose profile differs from the remembered one is announced to
 *   the subscribers. A first read is not — its caller gets the answer itself,
 *   and a subscriber holding a list only cares about entries it already shows.
 *
 * Pure: the read is injected (`fetchOne(uid)` → profile | null), so the
 * module has no Firebase import and the tests drive it with a fake clock.
 * Spec: the "Shared profile store (names and avatars)" bullet of
 * docs/spec/00-overview.md.
 */

export const PROFILE_TTL_MS = 10 * 60 * 1000;
export const MISSING_PROFILE_TTL_MS = 30 * 1000;

/** The fields a surface renders — the only ones a change is judged by. */
export function sameProfile(a, b) {
  if (!a || !b) return a === b;
  return (a.name || "") === (b.name || "")
    && (a.photoURL || null) === (b.photoURL || null)
    && (a.avatarPreference || null) === (b.avatarPreference || null);
}

export function createProfileStore({
  fetchOne,
  now = () => Date.now(),
  ttlMs = PROFILE_TTL_MS,
  missingTtlMs = MISSING_PROFILE_TTL_MS,
}) {
  const entries = new Map(); // uid → { profile: object | null, at: ms }
  const inflight = new Map(); // uid → Promise<void>
  const changeListeners = new Set();
  const settleListeners = new Set();

  const isFresh = (entry, t) => !!entry && t - entry.at < (entry.profile ? ttlMs : missingTtlMs);

  // Each uid is its own read, so one slow answer holds up only the callers
  // that asked for that uid.
  function readOne(uid) {
    const read = Promise.resolve()
      .then(() => fetchOne(uid))
      .then(profile => {
        inflight.delete(uid);
        const prev = entries.get(uid);
        const next = profile || null;
        entries.set(uid, { profile: next, at: now() });
        // A profile that disappeared is not announced: a subscriber keeps the
        // name it shows rather than blanking a row mid-session.
        if (prev && next && !sameProfile(prev.profile, next)) changeListeners.forEach(fn => fn([next]));
        settleListeners.forEach(fn => fn([uid]));
      }, () => { inflight.delete(uid); });
    inflight.set(uid, read);
  }

  /** The found profiles of `uids` (missing ones are left out), reading only what is not fresh. */
  async function load(uids) {
    const unique = [...new Set((uids || []).filter(Boolean))];
    if (!unique.length) return [];
    const t = now();
    unique.forEach(uid => {
      if (!inflight.has(uid) && !isFresh(entries.get(uid), t)) readOne(uid);
    });
    await Promise.all(unique.map(uid => inflight.get(uid)).filter(Boolean));
    return unique.map(uid => entries.get(uid)?.profile).filter(Boolean);
  }

  /**
   * What the store holds for `uid`, fresh or not, without reading:
   * `undefined` = never answered, `null` = no profile, else the profile.
   */
  function peek(uid) {
    const entry = entries.get(uid);
    return entry ? entry.profile : undefined;
  }

  /** `listener(changedProfiles)` on a re-read that changed a profile; returns the unsubscribe. */
  function subscribe(listener) {
    changeListeners.add(listener);
    return () => { changeListeners.delete(listener); };
  }

  /** `listener(uids)` whenever an answer for those uids was stored (first read included). */
  function onSettle(listener) {
    settleListeners.add(listener);
    return () => { settleListeners.delete(listener); };
  }

  return { load, peek, subscribe, onSettle };
}
